import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const DonationSectionEN = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const predefinedAmounts = [25, 50, 100, 250, 500];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleDonation = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    try {
      const { donationSchema } = await import('@/lib/validations');
      donationSchema.parse({ amount });
    } catch (error: any) {
      toast({
        title: "Invalid amount",
        description: error?.issues?.[0]?.message || "Please select or enter a valid amount to donate.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          amount: Math.round(amount * 100)
        }
      });

      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('Could not create payment link');
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: "Error processing donation",
        description: "We couldn't process your donation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="donation-section" className="py-20 bg-gradient-primary relative overflow-hidden">
      <div className="absolute inset-0 bg-primary/5"></div>
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Your donation 
            <span className="text-accent-orange"> saves lives</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Every donation brings us one step closer to finding cures for neurological diseases. 
            Your support makes our research possible.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-strong border-0 backdrop-blur-sm bg-card/95 dark:bg-card/95">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-foreground">
                Make a Donation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-3 block">
                  Select an amount:
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {predefinedAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant={selectedAmount === amount ? "default" : "outline"}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 ${
                        selectedAmount === amount 
                          ? "bg-primary hover:bg-primary-dark text-white shadow-medium" 
                          : "hover:bg-primary/10 hover:border-primary"
                      }`}
                    >
                      €{amount}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Or enter a custom amount:
                </label>
                <Input
                  type="number"
                  placeholder="Amount in EUR"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  className="text-lg py-3"
                />
              </div>

              <div className="bg-accent p-4 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  With <span className="font-semibold text-foreground">
                    €{customAmount || selectedAmount}
                  </span> we can fund{" "}
                  {customAmount || selectedAmount >= 250 
                    ? "a full week of laboratory research" 
                    : customAmount || selectedAmount >= 100
                    ? "advanced analysis of neurological samples"
                    : "essential materials for our experiments"
                  }
                </p>
              </div>

              <Button 
                size="lg" 
                className="w-full bg-secondary hover:bg-secondary-light text-white py-4 text-lg font-semibold shadow-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDonation}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : `Donate €${customAmount || selectedAmount}`}
              </Button>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg text-left space-y-4">
                <h4 className="font-semibold text-foreground text-sm">Discover all the benefits of donating to NeuroResearch Foundation</h4>
                
                <p className="text-xs text-muted-foreground">
                  Whether you are an individual or a legal entity, your donations to the NeuroResearch Foundation can be deducted from your income tax or corporate tax in Spain only. Our organization is covered by Law 49/2002, on the tax regime for non-profit entities and incentives for patronage. Additionally, Royal Decree-Law 6/2023 has improved deductions for donations to NGOs, so now collaborating has even more advantages.
                </p>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Deductions for individuals (Personal Income Tax)</h5>
                  <p className="text-xs text-muted-foreground mb-2">The percentage you can deduct depends on the amount and whether you donate continuously:</p>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Up to €250 per year → 80% deduction.</li>
                    <li>Amount exceeding €250 → 40% deduction.</li>
                    <li>If you donate for 3 years or more and maintain or increase your contribution → the percentage on the part exceeding €250 increases to 45%.</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Practical examples:</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>If you donate €250, the Tax Agency returns €200, so your real cost will be €50.</li>
                    <li>If you donate €400: First two years → €260 deduction.</li>
                    <li>From the third year onwards → €267.5 deduction.</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    Remember: If your autonomous community has additional deductions or its own regional regimes, the tax benefit may be even greater.
                  </p>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Deductions for companies (Corporate Tax)</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>40% general deduction.</li>
                    <li>50% if the donation is recurring (three years or more).</li>
                    <li>Limit: 15% of the tax base.</li>
                    <li>If you exceed the limit, the excess can be applied in the following 10 years.</li>
                  </ul>
                </div>

                <p className="text-xs text-primary font-medium">
                  If you want to support us, get in touch.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16 text-white text-center">
          <div>
            <div className="text-3xl font-bold mb-2">€2.5M</div>
            <div className="text-white/80">Raised in 2025</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">1,200+</div>
            <div className="text-white/80">Active donors</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">95%</div>
            <div className="text-white/80">Goes directly to research</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSectionEN;
